/**
 * 비로그인 업로드 "게재 준비" 상태 스트림(NDJSON `/public-upload/@:handle/upload-status`) 워처.
 * 이벤트 해석은 uploadStatusReducer.ts(순수)가 한다. 끊김(앱 전환·네트워크)은 visible/online 시
 * 재접속으로 복구한다 — 서버가 접속 시점 상태를 다시 보내 주므로 안전하다. 재접속이 거듭
 * 실패하면 "서버에서 계속돼요" 로 마무리한다(무한 대기 금지).
 */
import {
  applyUploadStatusEvent, initialUploadWatchState, UPLOAD_WATCH_TEXT,
  UploadStatusEvent, UploadWatchState,
} from './uploadStatusReducer';

export type { UploadWatchState } from './uploadStatusReducer';

const DETACH_AFTER_FAILURES = 3;

export interface WatchOptions {
  url: string;
  token: string;
  onState: (s: UploadWatchState) => void;
  /** 재접속 트리거를 붙일 window(테스트에서 주입). 기본 globalThis.window. */
  win?: Window;
}

/** NDJSON 을 읽어 onState 로 상태를 흘린다. 끊기면 visible/online 시 재접속(최대 3회 실패까지). stop() 으로 종료. */
export function watchUploadStatus(opts: WatchOptions): { stop: () => void } {
  const win = opts.win ?? (typeof window !== 'undefined' ? window : undefined);
  let state = initialUploadWatchState();
  let failures = 0;
  let stopped = false;
  let controller: AbortController | null = null;
  const emit = (s: UploadWatchState) => { state = s; opts.onState(s); };
  const terminal = () => state.kind === 'done' || state.kind === 'failed' || state.kind === 'detached';

  const connect = async () => {
    if (stopped || terminal()) return;
    controller?.abort();
    controller = new AbortController();
    try {
      const res = await fetch(opts.url, { headers: { 'x-upload-token': opts.token }, signal: controller.signal });
      if (!res.ok || !res.body) {
        // 401(승인/거절/만료)·409·503 — 더 볼 수 없는 상태. 서버에서는 계속된다.
        emit({ ...state, kind: 'detached', text: UPLOAD_WATCH_TEXT.detached, percent: null });
        return;
      }
      failures = 0;
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        let nl: number;
        while ((nl = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          if (!line) continue;
          let ev: UploadStatusEvent | null = null;
          try { ev = JSON.parse(line); } catch { ev = null; }
          if (ev) emit(applyUploadStatusEvent(state, ev));
          if (terminal()) { controller.abort(); return; }
        }
      }
      // 서버가 종료 라인 없이 닫음 → 재접속 대상
      if (!terminal()) scheduleReconnect();
    } catch (e) {
      if (stopped || (e as Error).name === 'AbortError') return;
      scheduleReconnect();
    }
  };

  const scheduleReconnect = () => {
    if (stopped || terminal()) return;
    failures++;
    if (failures >= DETACH_AFTER_FAILURES) {
      emit({ ...state, kind: 'detached', text: UPLOAD_WATCH_TEXT.detached, percent: null });
      return;
    }
    setTimeout(() => void connect(), 2000 * failures);
  };

  const onVisible = () => {
    if (win && win.document.visibilityState === 'visible' && !terminal()) void connect();
  };
  const onOnline = () => { if (!terminal()) void connect(); };
  win?.addEventListener('visibilitychange', onVisible);
  win?.addEventListener('online', onOnline);

  void connect();
  return {
    stop: () => {
      stopped = true;
      controller?.abort();
      win?.removeEventListener('visibilitychange', onVisible);
      win?.removeEventListener('online', onOnline);
    },
  };
}
